import { Button, Icon, Modal } from '@aidkitorg/component-library';
import React, { useCallback, useMemo } from 'react';
import { useLocalizedStrings } from '../../../Localization';
import { getTodayAsYYYYMMDD, toPascalCase } from '../../../utils/adminPanel.utils';
import type { NotificationRecipientsResult } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import * as XLSX from 'xlsx';
import { usePost } from '../../../API';
import type { NotificationDetails } from '@aidkitorg/types/lib/survey';

type NotificationRecipientsDownloadModalProps = {
  notificationName: string;
  variables: string[];
  downloadModalOpen: boolean;
  setDownloadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // New props for admin panel with filtering/column visibility
  filteredSentData?: NotificationRecipientsResult['sent'];
  filteredEligibleData?: NotificationRecipientsResult['eligible'];
  totalSentCount?: number;
  totalEligibleCount?: number;
  targetFields?: Array<{ id: string; label: string }>;
  sentColumnVisibility?: Record<string, boolean>;
  eligibleColumnVisibility?: Record<string, boolean>;
  pageConfig?: NotificationDetails;
  // Legacy props for Config pages (backward compatibility)
  sentData?: NotificationRecipientsResult['sent'];
  eligibleData?: NotificationRecipientsResult['eligible'];
}

export const NotificationRecipientsDownloadModal: React.FC<NotificationRecipientsDownloadModalProps> = ({
  notificationName,
  filteredSentData,
  filteredEligibleData,
  totalSentCount,
  totalEligibleCount,
  // Legacy props for backward compatibility
  sentData,
  eligibleData,
  variables,
  targetFields,
  downloadModalOpen,
  setDownloadModalOpen,
  sentColumnVisibility,
  eligibleColumnVisibility,
  pageConfig,
}) => {
  const L = useLocalizedStrings();
  const logAudit = usePost("/api/admin_panel/log_audit_entry");

  // Use new props if provided, otherwise fall back to legacy props
  const sentDataToUse = filteredSentData ?? sentData ?? [];
  const eligibleDataToUse = filteredEligibleData ?? eligibleData ?? [];
  const targetFieldsToUse = targetFields ?? [];
  const sentColumnVisibilityToUse = sentColumnVisibility ?? {};
  const eligibleColumnVisibilityToUse = eligibleColumnVisibility ?? {};

  const formattedNotificationName = toPascalCase(notificationName);

  const logDownloadEvent = useCallback(async (format: string, type?: 'sent' | 'eligible') => {
    try {
      await logAudit({
        eventName: "NOTIFICATION_RECIPIENTS_DOWNLOADED",
        value: {
          notificationName: formattedNotificationName,
          format: format,
          type: type,
          sentCount: sentDataToUse.length,
          eligibleCount: eligibleDataToUse.length,
          totalSentCount: totalSentCount ?? sentDataToUse.length,
          totalEligibleCount: totalEligibleCount ?? eligibleDataToUse.length,
        }
      });
    } catch (error) {
      console.error('Failed to log notification recipients download:', error);
    }
  }, [logAudit, formattedNotificationName, sentDataToUse.length, eligibleDataToUse.length, totalSentCount, totalEligibleCount]);

  // Get visible columns based on pageConfig and column visibility state
  // This logic must match the table's column filtering in NotificationRecipientsPage
  const getVisibleFields = useCallback((columnVisibility: Record<string, boolean>, isSentTab: boolean) => {
    const config = pageConfig?.defaultTableColumns;

    // Map of column id to data field name
    const columnToFieldMap: Record<string, string> = {
      'uid': 'uid',
      'legal_name': 'legal_name',
      'channel': 'channel',
      'status': 'status',
      'sentTimestamp': 'sent_timestamp',
    };

    // Start with base fields (uid and legal_name are always shown in table)
    const visibleFields = new Set(['uid', 'legal_name']);

    // Add fields based on pageConfig visibility (must match table logic)
    // When config is undefined, default to showing all columns (like the table does)
    if (!config || config.channel !== 'hidden') {
      visibleFields.add('channel');
    }
    if (isSentTab && (!config || config.status !== 'hidden')) {
      visibleFields.add('status');
    }
    if (isSentTab && (!config || config.sent !== 'hidden')) {
      visibleFields.add('sent_timestamp');
    }

    // Check if variables should be visible based on config
    // Must match table logic: when config exists, variables are shown only if config.variables === 'visible'
    // When config is undefined, show all columns (including variables)
    const showVariables = !config || config.variables === 'visible';

    // Remove fields that are hidden via column visibility state
    Object.entries(columnVisibility).forEach(([columnId, visible]) => {
      if (!visible) {
        const fieldName = columnToFieldMap[columnId];
        if (fieldName) {
          visibleFields.delete(fieldName);
        }
      }
    });

    return { visibleFields, showVariables, columnVisibility };
  }, [pageConfig]);

  const processDataForSheet = useCallback((
    data: NotificationRecipientsResult['sent'] | NotificationRecipientsResult['eligible'],
    isSentTab: boolean,
    columnVisibility: Record<string, boolean>
  ) => {
    const { visibleFields, showVariables } = getVisibleFields(columnVisibility, isSentTab);

    return data.map((recipient) => {
      const row: Record<string, string | undefined> = {};

      // Add base fields based on visibility
      if (visibleFields.has('uid')) row.uid = recipient.uid;
      if (visibleFields.has('legal_name')) row.legal_name = recipient.legal_name;
      if (visibleFields.has('channel')) row.channel = recipient.channel;

      // Include message and subject in export when there are visible columns selected
      if (visibleFields.size > 0) {
        row.message = recipient.message;
        row.subject = recipient.subject;
      }

      // Add sent-only fields
      if (isSentTab && visibleFields.has('status')) {
        row.status = recipient.status || '';
      }
      if (isSentTab && visibleFields.has('sent_timestamp')) {
        row.sent_timestamp = recipient.sentTimestamp ? new Date(recipient.sentTimestamp).toLocaleString() : '';
      }

      // Add variables if visible (check individual column visibility)
      if (showVariables) {
        variables.forEach(variable => {
          const columnId = `variable_${variable}`;
          // Only include if not explicitly hidden in column visibility
          if (columnVisibility[columnId] !== false) {
            row[variable] = recipient.variables?.[variable] || '';
          }
        });
      }

      // Add target fields if visible (check individual column visibility)
      if (showVariables) {
        targetFieldsToUse.forEach(field => {
          const columnId = `targetField_${field.id}`;
          // Only include if not explicitly hidden in column visibility
          if (columnVisibility[columnId] !== false) {
            row[field.label] = recipient.targetFields?.[field.id] || '';
          }
        });
      }

      return row;
    });
  }, [variables, targetFieldsToUse, getVisibleFields]);

  const processedSentData = useMemo(
    () => processDataForSheet(sentDataToUse, true, sentColumnVisibilityToUse),
    [sentDataToUse, processDataForSheet, sentColumnVisibilityToUse]
  );

  const processedEligibleData = useMemo(
    () => processDataForSheet(eligibleDataToUse, false, eligibleColumnVisibilityToUse),
    [eligibleDataToUse, processDataForSheet, eligibleColumnVisibilityToUse]
  );

  const downloadCSV = useCallback(async (data: Record<string, string | undefined>[], type: 'sent' | 'eligible') => {
    if (data.length === 0) return;

    const convertToCSV = (objArray: Record<string, string | undefined>[]): string => {
      // Get all unique keys from all objects to handle inconsistent keys
      const allKeys = Array.from(new Set(objArray.flatMap(obj => Object.keys(obj))));
      const header = allKeys.map(key => `"${key.replace(/"/g, '""')}"`).join(',');
      const rows = objArray.map(obj =>
        allKeys.map(key => {
          const val = obj[key] ?? '';
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(',')
      );
      return [header, ...rows].join('\n');
    };

    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const typeLabel = type === 'sent' ? L.admin_panel.notifications.sent : L.admin_panel.notifications.eligible;
    const fileName = `${formattedNotificationName}_${typeLabel}_Recipients_${getTodayAsYYYYMMDD()}.csv`;

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    await logDownloadEvent('CSV', type);
  }, [formattedNotificationName, L.admin_panel.notifications.sent, L.admin_panel.notifications.eligible, logDownloadEvent]);

  const downloadSentCSV = useCallback(async () => {
    await downloadCSV(processedSentData, 'sent');
  }, [downloadCSV, processedSentData]);

  const downloadEligibleCSV = useCallback(async () => {
    await downloadCSV(processedEligibleData, 'eligible');
  }, [downloadCSV, processedEligibleData]);

  const downloadAsXLSX = useCallback(async () => {
    if (processedSentData.length === 0 && processedEligibleData.length === 0) return;

    const workbook = XLSX.utils.book_new();

    // Create sent recipients sheet with consistent column ordering
    if (processedSentData.length > 0) {
      const sentKeys = Array.from(new Set(processedSentData.flatMap(obj => Object.keys(obj))));
      const sentWorksheet = XLSX.utils.json_to_sheet(processedSentData, { header: sentKeys });
      XLSX.utils.book_append_sheet(workbook, sentWorksheet, 'Sent Recipients');
    }

    // Create eligible recipients sheet with consistent column ordering
    if (processedEligibleData.length > 0) {
      const eligibleKeys = Array.from(new Set(processedEligibleData.flatMap(obj => Object.keys(obj))));
      const eligibleWorksheet = XLSX.utils.json_to_sheet(processedEligibleData, { header: eligibleKeys });
      XLSX.utils.book_append_sheet(workbook, eligibleWorksheet, 'Eligible Recipients');
    }

    const fileName = `${formattedNotificationName}_Recipients_${getTodayAsYYYYMMDD()}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    await logDownloadEvent('XLSX');
  }, [processedSentData, processedEligibleData, formattedNotificationName, logDownloadEvent]);

  return (
    <Modal 
      title={L.admin_panel.notifications.export_recipients} 
      open={downloadModalOpen} 
      onClose={() => setDownloadModalOpen(false)}
    >
      <div className="pb-4">
        {L.admin_panel.notifications.export_recipients_desc}
      </div>
      <div className="flex flex-col gap-y-4">
        {/* Excel Export Section */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">{L.admin_panel.notifications.xlsx_format}</div>
          <Button
            onClick={downloadAsXLSX}
            variant="secondary"
            className="!justify-start w-full"
            disabled={processedSentData.length === 0 && processedEligibleData.length === 0}
          >
            <div className="flex gap-4 items-center py-2">
              <Icon name="TableCellsIcon" role='decorative'/>
              <div className="!text-left">
                <div>{L.admin_panel.notifications.xlsx_format}</div>
                <div className="font-light">{L.admin_panel.notifications.xlsx_format_desc}</div>
              </div>
            </div>
          </Button>
        </div>

        {/* CSV Export Section */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">{L.admin_panel.notifications.csv_format}</div>
          <div className="flex flex-col gap-y-2">
            <Button 
              onClick={downloadSentCSV} 
              variant="secondary" 
              className="!justify-start w-full"
              isDisabled={processedSentData.length === 0}
            >
              <div className="flex gap-4 items-center py-2">
                <Icon name="ArrowDownTrayIcon" role='decorative'/>
                <div className="!text-left"> 
                  <div>{L.admin_panel.notifications.csv_format} - {L.admin_panel.notifications.sent}</div>
                  <div className="font-light">{L.admin_panel.notifications.csv_format_desc}</div>
                </div>
              </div>
            </Button>
            <Button 
              onClick={downloadEligibleCSV} 
              variant="secondary" 
              className="!justify-start w-full"
              isDisabled={processedEligibleData.length === 0}
            >
              <div className="flex gap-4 items-center py-2">
                <Icon name="ArrowDownTrayIcon" role='decorative'/>
                <div className="!text-left"> 
                  <div>{L.admin_panel.notifications.csv_format} - {L.admin_panel.notifications.eligible}</div>
                  <div className="font-light">{L.admin_panel.notifications.csv_format_desc}</div>
                </div>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};